import { useState, type ReactElement } from 'react';
import { AUTH_PATHS, discordAvatarUrlOfAccount, joinUrl } from '@luacentrix/api-client';
import type { SessionAccount } from '@luacentrix/contracts';
import { Avatar, Button, Menu, type AvatarSize, type MenuItem, type MenuProps } from '@luacentrix/ui';
import { useApp, type AccountStatus } from '../app-state';
import { useAccountSession } from '../data/queries';

export interface AccountSummary {
  name: string;
  handle: string | null;
  avatarUrl: string | null;
  status: AccountStatus;
}

type AnchorRect = MenuProps['anchor'];

const FALLBACK_ANCHOR: AnchorRect = { top: 0, left: 0, right: 0, bottom: 0, width: 0 };

function firstText(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmed = value?.trim() ?? '';
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return '';
}

export function accountSummaryOf(
  account: SessionAccount | null | undefined,
  status: AccountStatus,
): AccountSummary {
  const linkedName = firstText(account?.display_name);
  const username = firstText(account?.username);
  return {
    name:
      linkedName.length > 0
        ? linkedName
        : status === 'signed_in'
          ? 'Signed in'
          : status === 'pending'
            ? 'Checking session'
            : 'Not signed in',
    handle: username.length > 0 ? `@${username}` : null,
    avatarUrl: discordAvatarUrlOfAccount(account ?? null),
    status,
  };
}

export function AccountAvatar({
  summary,
  size = 32,
}: {
  summary: AccountSummary;
  size?: AvatarSize;
}): ReactElement {
  return (
    <Avatar
      name={summary.name}
      image={summary.avatarUrl ?? undefined}
      size={size}
      className="account-avatar"
    />
  );
}

export function AccountChip({ collapsed = false }: { collapsed?: boolean }): ReactElement {
  const { account, api } = useApp();
  const session = useAccountSession(api);
  const summary = accountSummaryOf(session.data?.account ?? null, account.status);
  return (
    <span className="account-chip" data-account-status={summary.status}>
      <AccountAvatar summary={summary} size={collapsed ? 24 : 32} />
      {collapsed ? null : (
        <span className="account-chip__text">
          <span className="account-chip__name">{summary.name}</span>
          <span className="account-chip__handle">
            {summary.handle ?? 'No Discord profile linked'}
          </span>
        </span>
      )}
    </span>
  );
}

export function signInUrlOf(baseUrl: string): string {
  return joinUrl(baseUrl, AUTH_PATHS.discordStart);
}

export function SignInAction({ label }: { label: string }): ReactElement {
  const { api } = useApp();
  return (
    <Button
      variant="primary"
      icon="lockOpen"
      onClick={() => {
        window.location.assign(signInUrlOf(api.config.baseUrl));
      }}
    >
      {label}
    </Button>
  );
}

export function AccountMenu(): ReactElement {
  const { account, api } = useApp();
  const session = useAccountSession(api);
  const summary = accountSummaryOf(session.data?.account ?? null, account.status);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect>(FALLBACK_ANCHOR);

  const signInItem: MenuItem = {
    id: 'account:signin',
    label: 'Sign in with Discord',
    icon: 'lockOpen',
    onSelect: () => {
      window.location.assign(signInUrlOf(api.config.baseUrl));
    },
  };

  const items: Array<MenuItem> =
    summary.status === 'sign_in_required'
      ? [
          signInItem,
          {
            id: 'account:refresh',
            label: 'Retry session check',
            icon: 'refresh',
            onSelect: account.refresh,
          },
        ]
      : [
          {
            id: 'account:refresh',
            label: summary.status === 'signed_in' ? 'Re-check session' : 'Retry session check',
            icon: 'refresh',
            onSelect: account.refresh,
          },
        ];

  return (
    <span className="account-menu">
      <button
        type="button"
        className="account-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${summary.name}`}
        onClick={event => {
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchor({
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
          });
          setOpen(current => !current);
        }}
      >
        <AccountAvatar summary={summary} size={32} />
        <span className="account-menu__status" data-account-status={summary.status}>
          {summary.status === 'signed_in'
            ? 'Signed in'
            : summary.status === 'pending'
              ? 'Checking'
              : 'Offline'}
        </span>
      </button>
      <Menu
        items={items}
        open={open}
        anchor={anchor}
        onClose={() => setOpen(false)}
        label="Account"
      />
    </span>
  );
}
